import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    background: theme.palette.mode === 'light'
      ? '#FFFFFF'
      : 'linear-gradient(145deg, #121212 0%, #1a1a1a 100%)',
    boxShadow: theme.palette.mode === 'light'
      ? '0 4px 20px 0 rgba(0, 0, 0, 0.1)'
      : '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
    border: theme.palette.mode === 'light'
      ? '1px solid rgba(0, 0, 0, 0.1)'
      : '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '16px',
  },
}));

const StyledImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: 'auto',
  borderRadius: '8px',
  boxShadow: theme.palette.mode === 'light'
    ? '0 4px 20px 0 rgba(0, 0, 0, 0.1)'
    : '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
}));

const GradientText = styled(Typography)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 700,
}));

const ActionButton = styled(Button)(({ actiontype, selected }) => ({
  backgroundColor: actiontype === 'approve' ? '#66FF00' : '#DC2626',
  color: '#fff',
  fontWeight: 600,
  opacity: selected ? 1 : 0.85,
  transform: selected ? 'scale(1.05)' : 'scale(1)',
  border: selected ? '2px solid #fff' : 'none',
  '&:hover': {
    backgroundColor: actiontype === 'approve' ? '#52cc00' : '#b81d1d',
  },
  textTransform: 'none',
  minWidth: 120,
}));

const ZoomDialog = ({
  image,
  open,
  isReview = false,
  onClose,
  onClickPrevious,
  onClickNext,
  onApprove,
  onReject,
  imageStatuses = {},
}) => {
  const imageStatus = imageStatuses?.[image?.identificador];
  const isApproved = imageStatus === 'approve';
  const isRejected = imageStatus === 'reject';
  const alreadyReviewed = isApproved || isRejected;

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIosNewIcon fontSize="small" />}
            onClick={onClickPrevious}
            disabled={!onClickPrevious}
            variant="text"
            size="small"
            sx={{
              textTransform: 'none',
              color: !onClickPrevious ? 'text.disabled' : 'text.primary',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            Anterior
          </Button>
          <Button
            endIcon={<ArrowForwardIosIcon fontSize="small" />}
            onClick={onClickNext}
            disabled={!onClickNext}
            variant="text"
            size="small"
            sx={{
              textTransform: 'none',
              color: !onClickNext ? 'text.disabled' : 'text.primary',
              '&:hover': {
                color: 'primary.main',
              },
            }}
          >
            Próxima
          </Button>
        </Box>
        <IconButton
          sx={{
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
            },
          }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Fade in timeout={500}>
          <Box sx={{ position: 'relative' }}>
            <StyledImage src={image?.fotoLink} alt="Imagem em zoom" />
            {image?.etapa && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 16,
                  left: 16,
                  background: 'rgba(0, 0, 0, 0.7)',
                  padding: '4px 8px',
                  borderRadius: '4px',
                }}
              >
                <GradientText variant="body2">
                  {image.etapa}
                </GradientText>
              </Box>
            )}
          </Box>
        </Fade>
      </DialogContent>
      {isReview && (
        <DialogActions sx={{ justifyContent: 'flex-end', px: 3, pb: 2 }}>
          <ActionButton
            actiontype="approve"
            selected={isApproved}
            onClick={() => onApprove(image)}
            disabled={alreadyReviewed || !image}
          >
            Aprovar
          </ActionButton>
          <ActionButton
            actiontype="reject"
            selected={isRejected}
            onClick={() => onReject(image)}
            disabled={alreadyReviewed || !image}
          >
            Reprovar
          </ActionButton>
        </DialogActions>
      )}
    </StyledDialog>
  );
};

export default ZoomDialog;
